// Derwent PDF Studio — native, Sejda-style PDF suite.
// Fully self-contained: uploads run through /api/pdf/* and download straight
// to the user. Zero persistence — the backend deletes every file the moment
// the response finishes streaming.
import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, FileText, Shield, Sparkles,
  Combine, Scissors, Minimize2, Trash2, RotateCw, FileType2,
  Upload, X, Download, Loader2, CheckCircle2, AlertCircle, Plus,
  ArrowUp, ArrowDown, GripVertical,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════
// Tool registry — each entry powers a card on the landing grid
// ═══════════════════════════════════════════════════════════════════
const TOOLS = [
  { id: 'merge',       icon: Combine,   title: 'Merge PDFs',      blurb: 'Combine multiple PDFs into one — drag to reorder.', tone: 'rose'   },
  { id: 'split',       icon: Scissors,  title: 'Split PDF',       blurb: 'Extract pages or split into multiple files.',      tone: 'orange' },
  { id: 'compress',    icon: Minimize2, title: 'Compress PDF',    blurb: 'Shrink file size without losing readability.',      tone: 'amber'  },
  { id: 'delete',      icon: Trash2,    title: 'Delete Pages',    blurb: 'Remove specific pages from your PDF.',              tone: 'red'    },
  { id: 'rotate',      icon: RotateCw,  title: 'Rotate Pages',    blurb: 'Rotate all pages or specific pages by 90°.',        tone: 'sky'    },
  { id: 'word-to-pdf', icon: FileType2, title: 'Word → PDF',      blurb: 'Convert .doc, .docx, .rtf, .odt to PDF.',           tone: 'blue'   },
];

const TONE_MAP = {
  rose:    { bg: 'bg-rose-50',    ring: 'ring-rose-200/60',    text: 'text-rose-700',    dot: 'bg-rose-500' },
  orange:  { bg: 'bg-orange-50',  ring: 'ring-orange-200/60',  text: 'text-orange-700',  dot: 'bg-orange-500' },
  amber:   { bg: 'bg-amber-50',   ring: 'ring-amber-200/60',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  red:     { bg: 'bg-red-50',     ring: 'ring-red-200/60',     text: 'text-red-700',     dot: 'bg-red-500' },
  sky:     { bg: 'bg-sky-50',     ring: 'ring-sky-200/60',     text: 'text-sky-700',     dot: 'bg-sky-500' },
  blue:    { bg: 'bg-blue-50',    ring: 'ring-blue-200/60',    text: 'text-blue-700',    dot: 'bg-blue-500' },
};

// ═══════════════════════════════════════════════════════════════════
// Main page
// ═══════════════════════════════════════════════════════════════════
export default function PdfStudio() {
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState(null);

  return (
    <div className="min-h-screen bg-[var(--page-bg)] flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button
          onClick={() => navigate('/')}
          data-testid="pdf-back"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-900 mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to CRM
        </button>

        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-white border border-slate-200/70 p-6 sm:p-8 mb-6 shadow-[0_2px_12px_-4px_rgba(15,23,42,0.06)]">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em]">
              <Sparkles className="w-3 h-3" /> PDF Studio
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 px-3 py-1 text-[10px] font-black uppercase tracking-wider">
              <Shield className="w-3 h-3" /> Files never stored
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-950 leading-[1.05]">
            Every PDF tool you need.<br/><span className="text-rose-600">Nothing kept afterwards.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-3 max-w-2xl">
            Merge, split, compress, edit and convert PDFs right inside Derwent.
            Files are processed in-memory and force-deleted the instant your download finishes.
          </p>
        </motion.section>

        {/* Tool grid */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TOOLS.map((t) => {
            const tone = TONE_MAP[t.tone];
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTool(t.id)}
                data-testid={`pdf-tool-${t.id}`}
                className="group text-left rounded-3xl bg-white border border-slate-200/70 p-5 hover:border-slate-900/20 hover:-translate-y-0.5 transition-all shadow-[0_2px_8px_-4px_rgba(15,23,42,0.04)] hover:shadow-[0_8px_24px_-8px_rgba(15,23,42,0.12)]">
                <div className="flex items-start justify-between mb-3">
                  <span className={`w-11 h-11 rounded-2xl ${tone.bg} ring-1 ${tone.ring} flex items-center justify-center`}>
                    <Icon className={`w-[22px] h-[22px] ${tone.text}`} strokeWidth={2.2} />
                  </span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 group-hover:text-slate-700 transition-colors">Open →</span>
                </div>
                <h3 className="text-base font-black text-slate-950 tracking-tight">{t.title}</h3>
                <p className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">{t.blurb}</p>
              </button>
            );
          })}
        </section>

        <p className="text-center text-[11px] text-slate-400 mt-8">
          PDF Studio · No files are stored on Derwent servers. Downloads are one-time streams.
        </p>
      </main>
      <Footer />

      <AnimatePresence>
        {activeTool && (
          <ToolModal
            toolId={activeTool}
            onClose={() => setActiveTool(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Modal shell — hosts the individual tool UIs
// ═══════════════════════════════════════════════════════════════════
function ToolModal({ toolId, onClose }) {
  const tool = TOOLS.find((t) => t.id === toolId);
  if (!tool) return null;
  const tone = TONE_MAP[tool.tone];
  const Icon = tool.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid={`pdf-modal-${toolId}`}>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl w-full max-w-2xl my-4 sm:my-0 shadow-[0_30px_60px_-15px_rgba(2,6,23,0.35)]">
        <div className="p-5 sm:p-6 border-b border-slate-100 flex items-center gap-3">
          <span className={`w-10 h-10 rounded-2xl ${tone.bg} ring-1 ${tone.ring} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${tone.text}`} strokeWidth={2.2} />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-black text-slate-950 tracking-tight">{tool.title}</h2>
            <p className="text-[12px] text-slate-500 leading-tight">{tool.blurb}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="pdf-modal-close"
            className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6">
          {toolId === 'merge' && <MergeTool onDone={onClose} />}
          {toolId === 'split' && <SplitTool onDone={onClose} />}
          {toolId === 'compress' && <CompressTool onDone={onClose} />}
          {toolId === 'delete' && <DeleteTool onDone={onClose} />}
          {toolId === 'rotate' && <RotateTool onDone={onClose} />}
          {toolId === 'word-to-pdf' && <WordTool onDone={onClose} />}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Shared UI bits
// ═══════════════════════════════════════════════════════════════════
function DropZone({ multiple, accept, onFiles, label = 'Drag & drop a PDF here, or click to browse', testid }) {
  const inputRef = useRef(null);
  const [isDrag, setIsDrag] = useState(false);
  const onDrop = useCallback((e) => {
    e.preventDefault(); setIsDrag(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) onFiles(files);
  }, [onFiles]);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDrag(true); }}
      onDragLeave={() => setIsDrag(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      data-testid={testid || 'pdf-dropzone'}
      className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-all
        ${isDrag ? 'border-slate-900 bg-slate-50' : 'border-slate-300 bg-slate-50/60 hover:border-slate-500 hover:bg-slate-50'}`}>
      <Upload className={`w-8 h-8 mx-auto mb-2 ${isDrag ? 'text-slate-900' : 'text-slate-400'}`} />
      <p className="text-[13px] font-semibold text-slate-700">{label}</p>
      <p className="text-[11px] text-slate-400 mt-1">Max 100 MB per file · files are deleted after download</p>
      <input
        ref={inputRef} type="file" accept={accept} multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onFiles(files);
          e.target.value = '';
        }} />
    </div>
  );
}

function BigButton({ children, onClick, disabled, testid, tone = 'slate' }) {
  const cls = tone === 'rose'
    ? 'bg-rose-600 hover:bg-rose-700'
    : 'bg-slate-950 hover:bg-slate-800';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full ${cls} disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-2xl px-5 py-3.5 text-[14px] font-black tracking-tight transition-colors flex items-center justify-center gap-2`}>
      {children}
    </button>
  );
}

function StatusBanner({ status, error }) {
  if (status === 'success') return (
    <div className="flex items-center gap-2 rounded-xl bg-emerald-50 ring-1 ring-emerald-200/60 px-3.5 py-2.5 text-[12.5px] font-semibold text-emerald-800">
      <CheckCircle2 className="w-4 h-4" /> Done — your file has downloaded.
    </div>
  );
  if (error) return (
    <div className="flex items-start gap-2 rounded-xl bg-rose-50 ring-1 ring-rose-200/60 px-3.5 py-2.5 text-[12.5px] font-semibold text-rose-800">
      <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{error}</span>
    </div>
  );
  return null;
}

// ═══════════════════════════════════════════════════════════════════
// Shared submit helper — POSTs multipart, triggers download, purges
// ═══════════════════════════════════════════════════════════════════
async function runTool(endpoint, formData, downloadName) {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`${API}/api/pdf/${endpoint}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    let msg = 'Something went wrong.';
    try {
      const j = await res.json();
      if (j?.detail) msg = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
    } catch { /* body wasn't JSON */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  // Guess filename from content-disposition if provided
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^";]+)"?/i);
  const name = match ? match[1] : downloadName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// ═══════════════════════════════════════════════════════════════════
// MERGE
// ═══════════════════════════════════════════════════════════════════
function MergeTool({ onDone }) {
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const addFiles = (incoming) => {
    const pdfs = incoming.filter((f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf');
    setFiles((prev) => [...prev, ...pdfs]);
  };
  const move = (idx, dir) => {
    setFiles((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const remove = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    setStatus('running'); setError('');
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      await runTool('merge', fd, 'merged.pdf');
      setStatus('success');
    } catch (e) {
      setStatus('idle'); setError(e.message || 'Merge failed.');
    }
  };

  return (
    <div className="space-y-4">
      {files.length === 0 ? (
        <DropZone
          multiple accept="application/pdf,.pdf"
          onFiles={addFiles}
          label="Drop PDFs here (2 or more), or click to select"
          testid="merge-drop"/>
      ) : (
        <>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="merge-file-list">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2">
                <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="text-[10px] font-black text-slate-400 w-5">{i + 1}.</span>
                <span className="text-[12.5px] font-semibold text-slate-800 flex-1 truncate">{f.name}</span>
                <span className="text-[10px] text-slate-400 flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded-md hover:bg-white disabled:opacity-30" data-testid={`merge-up-${i}`}><ArrowUp className="w-3.5 h-3.5"/></button>
                <button onClick={() => move(i, 1)} disabled={i === files.length - 1} className="p-1 rounded-md hover:bg-white disabled:opacity-30" data-testid={`merge-down-${i}`}><ArrowDown className="w-3.5 h-3.5"/></button>
                <button onClick={() => remove(i)} className="p-1 rounded-md hover:bg-white text-rose-600" data-testid={`merge-remove-${i}`}><X className="w-3.5 h-3.5"/></button>
              </div>
            ))}
          </div>
          <label className="inline-flex items-center gap-1.5 text-[12px] font-bold text-slate-700 hover:text-slate-900 cursor-pointer">
            <Plus className="w-3.5 h-3.5" /> Add more PDFs
            <input type="file" multiple accept="application/pdf,.pdf" className="hidden" onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }} data-testid="merge-add-more"/>
          </label>
        </>
      )}
      <StatusBanner status={status} error={error}/>
      <BigButton
        onClick={submit}
        disabled={files.length < 2 || status === 'running'}
        testid="merge-submit"
        tone="rose">
        {status === 'running'
          ? <><Loader2 className="w-4 h-4 animate-spin"/> Merging…</>
          : <><Download className="w-4 h-4"/> Merge {files.length || ''} PDFs → download</>}
      </BigButton>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SPLIT
// ═══════════════════════════════════════════════════════════════════
function SplitTool() {
  const [file, setFile] = useState(null);
  const [ranges, setRanges] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);

  const loadFile = async (f) => {
    setFile(f); setInfo(null); setError('');
    const fd = new FormData(); fd.append('file', f);
    const token = localStorage.getItem('auth_token');
    try {
      const r = await fetch(`${API}/api/pdf/info`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (r.ok) setInfo(await r.json());
    } catch { /* non-fatal */ }
  };

  const submit = async () => {
    if (!file || !ranges.trim()) return;
    setStatus('running'); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('ranges', ranges.trim());
      const isMulti = ranges.includes(';');
      await runTool('split', fd, isMulti ? 'split.zip' : 'split.pdf');
      setStatus('success');
    } catch (e) {
      setStatus('idle'); setError(e.message || 'Split failed.');
    }
  };

  return (
    <div className="space-y-4">
      {!file ? (
        <DropZone accept="application/pdf,.pdf" onFiles={(fs) => loadFile(fs[0])} testid="split-drop"/>
      ) : (
        <FilePill file={file} pages={info?.pages} onClear={() => { setFile(null); setInfo(null); setRanges(''); }} testid="split-file"/>
      )}
      <div>
        <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Pages / ranges</label>
        <input
          value={ranges}
          onChange={(e) => setRanges(e.target.value)}
          placeholder="e.g. 1-3, 5, 8-10   —   use ';' to create multiple files"
          className="w-full rounded-xl bg-slate-50 border border-slate-200 focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-900/10 outline-none px-4 py-2.5 text-[13px] font-mono"
          data-testid="split-ranges"/>
        <p className="text-[11px] text-slate-500 mt-1.5">
          {info?.pages ? `PDF has ${info.pages} pages. ` : ''}
          <b>Single file:</b> <code className="bg-slate-100 px-1 rounded">1-3,5</code> · <b>Multiple files:</b> <code className="bg-slate-100 px-1 rounded">1-3;5-7;9</code>
        </p>
      </div>
      <StatusBanner status={status} error={error}/>
      <BigButton
        onClick={submit}
        disabled={!file || !ranges.trim() || status === 'running'}
        testid="split-submit"
        tone="rose">
        {status === 'running'
          ? <><Loader2 className="w-4 h-4 animate-spin"/> Splitting…</>
          : <><Download className="w-4 h-4"/> Split → download</>}
      </BigButton>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// COMPRESS
// ═══════════════════════════════════════════════════════════════════
function CompressTool() {
  const [file, setFile] = useState(null);
  const [level, setLevel] = useState('recommended');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const submit = async () => {
    if (!file) return;
    setStatus('running'); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('level', level);
      await runTool('compress', fd, 'compressed.pdf');
      setStatus('success');
    } catch (e) {
      setStatus('idle'); setError(e.message || 'Compress failed.');
    }
  };

  return (
    <div className="space-y-4">
      {!file
        ? <DropZone accept="application/pdf,.pdf" onFiles={(fs) => setFile(fs[0])} testid="compress-drop"/>
        : <FilePill file={file} onClear={() => setFile(null)} testid="compress-file"/>}
      <div>
        <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-2">Compression level</label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: 'low', label: 'Low', hint: 'Best quality' },
            { v: 'recommended', label: 'Recommended', hint: 'Balanced' },
            { v: 'high', label: 'High', hint: 'Smallest file' },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setLevel(opt.v)}
              data-testid={`compress-level-${opt.v}`}
              className={`rounded-xl px-3 py-2.5 text-center transition-all ring-1
                ${level === opt.v
                  ? 'bg-slate-950 text-white ring-slate-950'
                  : 'bg-white ring-slate-200 text-slate-700 hover:ring-slate-400'}`}>
              <div className="text-[12.5px] font-black tracking-tight">{opt.label}</div>
              <div className={`text-[10px] font-semibold mt-0.5 ${level === opt.v ? 'text-white/60' : 'text-slate-400'}`}>{opt.hint}</div>
            </button>
          ))}
        </div>
      </div>
      <StatusBanner status={status} error={error}/>
      <BigButton onClick={submit} disabled={!file || status === 'running'} testid="compress-submit" tone="rose">
        {status === 'running'
          ? <><Loader2 className="w-4 h-4 animate-spin"/> Compressing…</>
          : <><Download className="w-4 h-4"/> Compress → download</>}
      </BigButton>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DELETE PAGES
// ═══════════════════════════════════════════════════════════════════
function DeleteTool() {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState('');
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const loadFile = async (f) => {
    setFile(f); setInfo(null); setError('');
    const fd = new FormData(); fd.append('file', f);
    const token = localStorage.getItem('auth_token');
    try {
      const r = await fetch(`${API}/api/pdf/info`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (r.ok) setInfo(await r.json());
    } catch { /* ignore */ }
  };

  const submit = async () => {
    if (!file || !pages.trim()) return;
    setStatus('running'); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('pages', pages.trim());
      await runTool('delete-pages', fd, 'trimmed.pdf');
      setStatus('success');
    } catch (e) {
      setStatus('idle'); setError(e.message || 'Delete pages failed.');
    }
  };

  return (
    <div className="space-y-4">
      {!file
        ? <DropZone accept="application/pdf,.pdf" onFiles={(fs) => loadFile(fs[0])} testid="delete-drop"/>
        : <FilePill file={file} pages={info?.pages} onClear={() => { setFile(null); setInfo(null); setPages(''); }} testid="delete-file"/>}
      <div>
        <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Pages to delete</label>
        <input
          value={pages}
          onChange={(e) => setPages(e.target.value)}
          placeholder="e.g. 2, 4-5, 9"
          className="w-full rounded-xl bg-slate-50 border border-slate-200 focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-900/10 outline-none px-4 py-2.5 text-[13px] font-mono"
          data-testid="delete-pages-input"/>
        {info?.pages && <p className="text-[11px] text-slate-500 mt-1.5">PDF has {info.pages} pages.</p>}
      </div>
      <StatusBanner status={status} error={error}/>
      <BigButton onClick={submit} disabled={!file || !pages.trim() || status === 'running'} testid="delete-submit" tone="rose">
        {status === 'running'
          ? <><Loader2 className="w-4 h-4 animate-spin"/> Deleting…</>
          : <><Download className="w-4 h-4"/> Delete pages → download</>}
      </BigButton>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ROTATE
// ═══════════════════════════════════════════════════════════════════
function RotateTool() {
  const [file, setFile] = useState(null);
  const [angle, setAngle] = useState(90);
  const [pages, setPages] = useState('all');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const submit = async () => {
    if (!file) return;
    setStatus('running'); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('angle', angle); fd.append('pages', pages.trim() || 'all');
      await runTool('rotate', fd, 'rotated.pdf');
      setStatus('success');
    } catch (e) {
      setStatus('idle'); setError(e.message || 'Rotate failed.');
    }
  };

  return (
    <div className="space-y-4">
      {!file
        ? <DropZone accept="application/pdf,.pdf" onFiles={(fs) => setFile(fs[0])} testid="rotate-drop"/>
        : <FilePill file={file} onClear={() => setFile(null)} testid="rotate-file"/>}
      <div>
        <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-2">Rotate by</label>
        <div className="grid grid-cols-3 gap-2">
          {[90, 180, 270].map((a) => (
            <button
              key={a}
              onClick={() => setAngle(a)}
              data-testid={`rotate-angle-${a}`}
              className={`rounded-xl px-3 py-2.5 text-[13px] font-black transition-all ring-1
                ${angle === a ? 'bg-slate-950 text-white ring-slate-950' : 'bg-white ring-slate-200 text-slate-700 hover:ring-slate-400'}`}>
              {a}°
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[11px] font-black uppercase tracking-wider text-slate-500 block mb-1.5">Pages</label>
        <input
          value={pages} onChange={(e) => setPages(e.target.value)}
          placeholder="all, or e.g. 1,3-5"
          className="w-full rounded-xl bg-slate-50 border border-slate-200 focus:border-slate-900 focus:bg-white focus:ring-4 focus:ring-slate-900/10 outline-none px-4 py-2.5 text-[13px] font-mono"
          data-testid="rotate-pages"/>
      </div>
      <StatusBanner status={status} error={error}/>
      <BigButton onClick={submit} disabled={!file || status === 'running'} testid="rotate-submit" tone="rose">
        {status === 'running'
          ? <><Loader2 className="w-4 h-4 animate-spin"/> Rotating…</>
          : <><Download className="w-4 h-4"/> Rotate → download</>}
      </BigButton>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// WORD → PDF
// ═══════════════════════════════════════════════════════════════════
function WordTool() {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const submit = async () => {
    if (!file) return;
    setStatus('running'); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const base = (file.name.replace(/\.[^.]+$/, '') || 'document') + '.pdf';
      await runTool('word-to-pdf', fd, base);
      setStatus('success');
    } catch (e) {
      setStatus('idle'); setError(e.message || 'Conversion failed.');
    }
  };

  return (
    <div className="space-y-4">
      {!file
        ? <DropZone
            accept=".doc,.docx,.rtf,.odt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onFiles={(fs) => setFile(fs[0])}
            label="Drop a Word file (.doc/.docx/.rtf/.odt) here, or click to browse"
            testid="word-drop"/>
        : <FilePill file={file} onClear={() => setFile(null)} testid="word-file"/>}
      <StatusBanner status={status} error={error}/>
      <BigButton onClick={submit} disabled={!file || status === 'running'} testid="word-submit" tone="rose">
        {status === 'running'
          ? <><Loader2 className="w-4 h-4 animate-spin"/> Converting…</>
          : <><Download className="w-4 h-4"/> Convert to PDF → download</>}
      </BigButton>
      <p className="text-[11px] text-slate-400 leading-relaxed">
        Powered by LibreOffice headless. Complex layouts, tables, embedded images and fonts are preserved.
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Reused file pill (after upload)
// ═══════════════════════════════════════════════════════════════════
function FilePill({ file, pages, onClear, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-4 py-3" data-testid={testid}>
      <span className="w-10 h-10 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center">
        <FileText className="w-5 h-5 text-rose-600" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-black text-slate-900 truncate">{file.name}</div>
        <div className="text-[11px] text-slate-500">
          {(file.size / 1024).toFixed(0)} KB{pages ? ` · ${pages} pages` : ''}
        </div>
      </div>
      <button
        onClick={onClear}
        className="p-2 rounded-lg hover:bg-white text-slate-500 hover:text-slate-900"
        data-testid="file-pill-clear">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
